import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { PublishJob } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { PlatformChip, PublishChip } from "@/components/shared/Chips";
import { Button } from "@/components/ui/button";

export const RetryCenter: React.FC = () => {
  const qc = useQueryClient();
  const jobs = useQuery<PublishJob[]>({
    queryKey: ["failed-jobs"],
    queryFn: async () => (await api.get("/publish/failed/list")).data,
    refetchInterval: 4000,
  });

  const retry = useMutation({
    mutationFn: async (jobId: string) => (await api.post(`/publish/${jobId}/retry`)).data,
    onSuccess: () => {
      toast.success("Retry queued");
      qc.invalidateQueries({ queryKey: ["failed-jobs"] });
    },
  });

  return (
    <DashboardLayout
      title="Retry Center"
      subtitle="Failed publishing jobs — one click to retry, raw video preserved until success."
      seoPath="/retry"
      seoDescription="Failed publishing jobs — one click retry with raw media preserved until success."
    >
      {jobs.isLoading ? (
        <div className="sg-skeleton h-64" />
      ) : jobs.data && jobs.data.length > 0 ? (
        <div className="space-y-4">
          {jobs.data.map((j) => (
            <div key={j.job_id} className="sg-card p-5" data-testid={`failed-job-${j.job_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-[color:var(--sg-danger)]" />
                    <PublishChip status={j.overall_status} />
                    <span className="text-xs text-[color:var(--sg-muted)]">
                      {new Date(j.updated_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="font-display text-lg font-semibold tracking-tight">
                    {j.caption}
                  </div>
                  <div className="mt-3 space-y-2">
                    {j.targets.map((t) => (
                      <div key={t.target_id} className="flex items-center gap-2 text-sm">
                        <PlatformChip platform={t.platform} />
                        <PublishChip status={t.status} />
                        {t.error && (
                          <span className="text-xs text-[color:var(--sg-danger)] truncate">
                            {t.error}
                          </span>
                        )}
                        {t.retry_count > 0 && (
                          <span className="text-xs text-[color:var(--sg-muted)]">
                            · retries: {t.retry_count}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  data-testid={`retry-btn-${j.job_id}`}
                  onClick={() => retry.mutate(j.job_id)}
                  disabled={retry.isPending}
                  className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
                >
                  {retry.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Retry</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No failed jobs"
          description="You're all clear. Your publishing pipeline is healthy."
        />
      )}
    </DashboardLayout>
  );
};
